// Tela Atividade — tipos, helpers visuais e constantes
// Espelha shape do RPC public.get_activity_feed (raios/supabase/migrations/...)
// Mantido propositadamente livre de dependencias React para facilitar teste/uso fora do componente.

import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Ban,
  AlertCircle,
  type LucideIcon,
} from 'lucide-react';

/* ── Tipos ──────────────────────────────────────────────────────────────────── */

export type ActivityKind = 'agent' | 'skill' | 'ephemeral' | 'handler' | 'companion';

// Vocabulario unificado (pos-normalizacao no RPC).
// 'partial' eh status proprio: skill_executions usa quando step parou no meio mas
// houve output util. NAO mapear para completed nem failed — entrega informacao distinta.
export type ActivityStatus =
  | 'running'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'cancelled'
  | 'partial';

export interface ActivityItem {
  kind: ActivityKind;
  id: string;
  actor_id: string | null;
  actor_slug: string | null;
  actor_name: string | null;
  actor_archetype: string | null;
  actor_role: string | null;
  actor_scope: string | null;
  parent_actor_id: string | null;
  parent_actor_slug: string | null;
  parent_actor_name: string | null;
  invoker_type: string | null; // 'agent' | 'standalone' | 'spawn' | null
  status: ActivityStatus;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  cost_usd: number | null;
  tokens_input: number | null;
  tokens_output: number | null;
  model_used: string | null;
  task_id: string | null;
  trace_id: string | null;
  error_message: string | null;
  // Opcao B (cost tracking): hierarquia para drill-down do companion-loop.
  // Filhas (LLM call, tool call) tem parent_trace_id != null; root tem parent_trace_id=null.
  parent_trace_id: string | null;
  root_trace_id: string | null;
  // metadata.kind do row: 'turn_root' | 'llm_call' | 'tool_call' | null (skill/ephemeral).
  metadata_kind: ActivityMetadataKind | null;
  // Opcao D (turn_root rich UI): counts agregados de filhas. Preenchido pelo
  // agent-loop no UPDATE final do turn_root; null em llm_call/tool_call/skill/ephemeral.
  llm_calls_count: number | null;
  tool_calls_count: number | null;
  // Plano-tela-atividade-canonica-v2 PR-2: target_slug expoe o quê o handler
  // executou (ex: '@tool-runner' → target='execute_db_operation'). Pra kinds
  // sem target distinto (agent/skill/wizard/ephemeral/companion), vem null.
  target_slug: string | null;
  // Plano-tela-atividade-canonica-v2 PR-5 (realtime SUM): durante status='running'
  // do pai, a UI mostra o agregado parcial das filhas via realtime (cost/tokens).
  // Triggers DB so populam cost_usd oficial em terminal, entao SUM "ao vivo"
  // precisa ser calculado client-side. Quando pai termina, cost_usd oficial
  // sobrescreve o display. null = nao ha filhas em memoria OU pai ja terminou.
  cost_usd_partial: number | null;
  tokens_input_partial: number | null;
  tokens_output_partial: number | null;
  children_completed_count: number | null;
}

export type ActivityMetadataKind = 'turn_root' | 'llm_call' | 'tool_call';

// Shape rico retornado por GET /v1/activity/:kind/:id (row completa por kind).
// Os campos extras dependem do kind e podem ser omitidos.
export interface ActivityDetail extends ActivityItem {
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown> | null;
  // Agent-specific
  input_prompt?: string | null;
  output_result?: string | null;
  files_created?: string[] | null;
  files_modified?: string[] | null;
  decision_rationale?: Record<string, unknown> | null;
  supervision_mode?: string | null;
  completed_at?: string | null;
  // Skill-specific
  skill_version?: string | null;
  instance_id?: string | null;
  trigger_summary?: string | null;
  error?: unknown;
  // Ephemeral-specific
  runner_type?: string | null;
  spawn_reason?: string | null;
}

export interface ActivityListResponse {
  items: ActivityItem[];
  next_cursor: { ts: string; id: string } | null;
}

export interface AgentMeta {
  id: string;
  agent_id: string;
  slug: string;
  name: string;
  archetype: string | null;
  role: string | null;
}

export interface SkillMeta {
  id: string;
  slug: string;
  name: string;
  scope: string | null;
}

/* ── Constantes PT-BR ───────────────────────────────────────────────────────── */

export const KIND_LABELS_PTBR: Record<ActivityKind, string> = {
  agent: 'Agente',
  skill: 'Skill',
  ephemeral: 'Efêmera',
  handler: 'Handler',
  companion: 'Companion',
};

export const STATUS_LABELS_PTBR: Record<ActivityStatus, string> = {
  running: 'Em execução',
  completed: 'Concluído',
  failed: 'Falhou',
  timeout: 'Timeout',
  cancelled: 'Cancelado',
  partial: 'Parcial',
};

// Mapping conhecido de runner_type para label humanizado.
// Fallback em getRunnerLabel cobre runners novos.
export const RUNNER_TYPE_LABELS: Record<string, string> = {
  mmos_clone: 'Clone MMOS',
  dc_batch_runner: 'Batch DC',
};

export function getRunnerLabel(runnerType: string | null | undefined): string {
  if (!runnerType) return 'Runner efêmero';
  if (RUNNER_TYPE_LABELS[runnerType]) return RUNNER_TYPE_LABELS[runnerType];
  // Fallback: snake_case -> Title Case
  return runnerType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ── Action label PT-BR ─────────────────────────────────────────────────────── */

export function getActionLabel(item: ActivityItem): string {
  const isInline = item.invoker_type === 'agent';
  const isStandalone = item.invoker_type === 'standalone';

  if (item.kind === 'agent') {
    switch (item.status) {
      case 'running': return 'em execução';
      case 'completed': return 'executou';
      case 'failed': return 'falhou ao executar';
      case 'timeout': return 'expirou tempo';
      case 'cancelled': return 'cancelou execução';
      case 'partial': return 'execução parcial';
    }
  }
  if (item.kind === 'skill') {
    const verb = isInline ? 'usou skill' : isStandalone ? 'invocou skill' : 'executou skill';
    switch (item.status) {
      case 'running': return `${verb} (em execução)`;
      case 'completed': return verb;
      case 'failed': return `${verb} — falhou`;
      case 'timeout': return `${verb} — timeout`;
      case 'cancelled': return `${verb} — cancelada`;
      case 'partial': return `${verb} — parcial`;
    }
  }
  if (item.kind === 'handler') {
    switch (item.status) {
      case 'running': return 'handler em execução';
      case 'completed': return 'handler executou';
      case 'failed': return 'handler falhou';
      case 'timeout': return 'handler timeout';
      case 'cancelled': return 'handler cancelado';
      case 'partial': return 'handler parcial';
    }
  }
  if (item.kind === 'companion') {
    switch (item.status) {
      case 'running': return 'turno em execução';
      case 'completed': return 'turno concluído';
      case 'failed': return 'turno falhou';
      case 'timeout': return 'turno timeout';
      case 'cancelled': return 'turno cancelado';
      case 'partial': return 'turno parcial';
    }
  }
  // ephemeral
  switch (item.status) {
    case 'running': return 'spawn em execução';
    case 'completed': return 'runner concluído';
    case 'failed': return 'runner falhou';
    case 'timeout': return 'runner timeout';
    case 'cancelled': return 'runner cancelado';
    case 'partial': return 'runner parcial';
  }
}

/* ── Status visual tokens ───────────────────────────────────────────────────── */

export interface StatusToken {
  bg: string;
  text: string;
  ring: string;
}

export function getStatusToken(status: ActivityStatus): StatusToken {
  switch (status) {
    case 'running':
      return {
        bg: 'bg-raios-accent/15',
        text: 'text-raios-accent-text',
        ring: 'ring-raios-accent/30',
      };
    case 'completed':
      return {
        bg: 'bg-raios-success/15',
        text: 'text-raios-success-text',
        ring: 'ring-raios-success/30',
      };
    case 'failed':
      return {
        bg: 'bg-raios-danger/15',
        text: 'text-raios-danger-text',
        ring: 'ring-raios-danger/30',
      };
    case 'timeout':
      return {
        bg: 'bg-raios-warning/15',
        text: 'text-raios-warning-text',
        ring: 'ring-raios-warning/30',
      };
    case 'cancelled':
      return {
        bg: 'bg-raios-elevated/50',
        text: 'text-raios-muted',
        ring: 'ring-raios-border',
      };
    case 'partial':
      return {
        bg: 'bg-raios-warning/15',
        text: 'text-raios-warning-text',
        ring: 'ring-raios-warning/30',
      };
  }
}

export function getStatusIcon(status: ActivityStatus): LucideIcon {
  switch (status) {
    case 'running':   return Loader2;
    case 'completed': return CheckCircle2;
    case 'failed':    return AlertTriangle;
    case 'timeout':   return Clock;
    case 'cancelled': return Ban;
    case 'partial':   return AlertCircle;
  }
}

/* ── Sanitizers ─────────────────────────────────────────────────────────────── */

const ERROR_MAX_BYTES = 1024;
// chars de controle (\x00-\x08, \x0B-\x1F, \x7F) — preservamos \t \n \r intencionalmente
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x08\x0B-\x1F\x7F]/g;

export function sanitizeErrorMessage(err: unknown): string | null {
  if (err === null || err === undefined) return null;
  let raw: string;
  if (typeof err === 'string') {
    raw = err;
  } else if (err instanceof Error) {
    raw = err.message;
  } else {
    try {
      raw = JSON.stringify(err);
    } catch {
      raw = String(err);
    }
  }
  // React escapa por default em {expression}, entao XSS via texto puro nao eh
  // problema. Removemos chars de controle (CONTROL_CHARS_RE no topo do arquivo)
  // para evitar quebra de copy/terminal quando admin copia o erro.
  const stripped = raw.replace(CONTROL_CHARS_RE, '').trim();
  if (stripped.length === 0) return null;
  if (stripped.length <= ERROR_MAX_BYTES) return stripped;
  return stripped.slice(0, ERROR_MAX_BYTES) + '… (truncado)';
}

/* ── Timestamps ─────────────────────────────────────────────────────────────── */

export function formatAbsoluteTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/* ── Period filter helper ───────────────────────────────────────────────────── */

export type PeriodFilter = 'today' | '7d' | '30d' | 'all';

export function periodToSinceISO(period: PeriodFilter): string | null {
  const now = new Date();
  switch (period) {
    case 'today': {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return d.toISOString();
    }
    case '7d': {
      const d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return d.toISOString();
    }
    case '30d': {
      const d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return d.toISOString();
    }
    case 'all':
      return null;
  }
}

// ═══ F3 2026-07-19 — formatador ÚNICO de "quem executou o quê" ═══
//
// Regra do dono (aprovada em mockup): as linhas/granularidade NÃO mudam — só o
// NOME de quem executou. Sempre que há um AGENTE por trás da ação, mostra o
// nome do agente ("Jeremias executou como agente"); sem agente, runner honesto
// ("@tool-runner executou a tool X"). O backend (RPC get_activity_feed +
// GET /demands/:id/executions) agora resolve o nome real em actor_name.

export interface ActorLead {
  /** Nome em negrito (agente real ou runner @x). */
  display: string;
  /** Slug em <code> logo após o nome (opcional). */
  code?: string | null;
  /** Verbo/ação em PT. */
  verb: string;
  /** Alvo em <code> após o verbo (opcional). */
  target?: string | null;
}

function statusSuffix(status: ActivityStatus): string {
  switch (status) {
    case 'running': return ' (em execução)';
    case 'failed': return ' — falhou';
    case 'timeout': return ' — timeout';
    case 'cancelled': return ' — cancelada';
    case 'partial': return ' — parcial';
    default: return '';
  }
}

/** O actor_name é um agente RESOLVIDO (não um runner @x)? */
function isResolvedAgentName(name: string | null | undefined): boolean {
  return !!name && !name.startsWith('@') && !/runner|handler/i.test(name);
}

// Runners de INFRAESTRUTURA (@x-runner) — nomes PT legíveis no lugar do slug
// técnico cru (admin cobrou 3x: "@task-runner", "@ephemeral-runner" na tela).
const RUNNER_DISPLAY_PTBR: Record<string, string> = {
  '@task-runner': 'Execução de tarefa',
  '@tool-runner': 'Ferramenta',
  '@skill-runner': 'Execução de skill',
  '@ephemeral-runner': 'Processo efêmero',
  '@demand-runner': 'Demanda',
  '@pipeline-runner': 'Pipeline',
  '@agent-runner': 'Agente',
};

/** Humaniza o nome de um runner/handler: @x-runner → label PT; senão tira o @. */
export function humanizeRunnerName(name: string | null | undefined): string {
  if (!name) return 'Processo';
  const known = RUNNER_DISPLAY_PTBR[name];
  if (known) return known;
  return name
    .replace(/^@/, '')
    .replace(/-/g, ' ')
    .replace(/\brunner\b/i, '')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Processo';
}

// ── Nomes HUMANOS das tools em PT (feed 2026-08-04) ───────────────────────────
// RÉPLICA do mapa canônico do backend (fonte da verdade):
//   raios-backend/src/experience/services/telegram-execution-history.ts
//   (TOOL_HUMAN_LABELS — o mesmo vocabulário que o usuário já vê no Telegram).
// Regra do dono: nome humano em português no lugar do slug técnico
// ("run_shell" → "Rodando comando"), sem "Ferramenta usou a ferramenta X".
// Ao mexer aqui, mexa lá (e vice-versa) — é UMA voz de produto em 2 superfícies.
export const TOOL_HUMAN_LABELS_PTBR: Readonly<Record<string, string>> = {
  append_execution_notes: 'registrando notas da execução',
  browse_url: 'acessando página',
  capture_website_screenshot: 'capturando página',
  search_memory: 'buscando memória',
  save_memory: 'salvando memória',
  create_agent: 'criando agente',
  create_business: 'criando negócio',
  create_demand: 'criando demanda',
  create_integration: 'criando integração',
  create_pipeline: 'criando pipeline',
  create_project: 'criando projeto',
  create_skill: 'criando skill',
  create_squad: 'criando Squad',
  create_tool: 'criando tool',
  delete_tenant_secret: 'removendo segredo',
  dispatch_raios_pipeline: 'despachando pipeline',
  enqueue_companion_relay: 'encaminhando mensagem para outro Companion',
  invoke_agent: 'invocando agente',
  invoke_mcp_server: 'acionando conector MCP',
  run_script_async: 'rodando script',
  execute_db_operation: 'acessando banco de dados',
  execute_raios_operation: 'executando operação no RAIOS',
  execute_raw_sql: 'executando operação SQL',
  fetch_assets: 'buscando ativos',
  fetch_asset_schema: 'entendendo estrutura do ativo',
  generate_openai_image: 'gerando imagem',
  get_connector_connections: 'consultando conexões',
  get_tenant_secret: 'consultando segredo',
  list_connectors: 'listando conectores',
  list_tenant_secrets: 'listando segredos',
  manage_loops: 'gerenciando loops',
  orchestrate_subagents: 'orquestrando agentes',
  promote_asset: 'promovendo ativo',
  query_activity_log: 'consultando histórico de atividades',
  read_governance: 'consultando governança',
  read_skill_reference: 'lendo referência da skill',
  read_system_map: 'consultando mapa do sistema',
  save_decision: 'salvando decisão',
  register_anti_pattern: 'registrando antipadrão',
  register_pending_callback: 'agendando monitoramento',
  github_action: 'executando ação no GitHub',
  instantiate_asset: 'instanciando ativo',
  send_document: 'enviando documento',
  storage_upload: 'enviando para o storage',
  storage_download: 'baixando do storage',
  storage_list: 'listando arquivos do storage',
  storage_signed_url: 'preparando acesso ao arquivo',
  sync_asset_storage: 'sincronizando ativo no storage',
  run_shell: 'rodando comando',
  read_file: 'lendo arquivo',
  write_file: 'escrevendo arquivo',
  list_directory: 'listando diretório',
  session_search: 'buscando na conversa',
  set_tenant_secret: 'salvando segredo',
  squad_lifecycle: 'gerenciando ciclo da Squad',
  start_pipeline_run: 'iniciando pipeline',
  update_agent: 'atualizando agente',
  update_demand_status: 'atualizando demanda',
  update_pipeline: 'atualizando pipeline',
  update_skill: 'atualizando skill',
  update_squad: 'atualizando Squad',
  update_tenant_settings: 'atualizando configurações',
  update_tool: 'atualizando tool',
  upload_artifact: 'publicando artefato',
  use_connector: 'usando conector',
  use_skill: 'aplicando skill',
  web_fetch: 'lendo página da web',
  web_search: 'buscando na web',
  // Capabilities usadas por domain workers e reconstruídas no mesmo ledger.
  edit: 'editando arquivo',
  file_io: 'manipulando arquivo',
  commit: 'salvando alterações no repositório',
  gh_pr_create: 'abrindo pull request',
  gh_pr_squash_merge: 'integrando pull request',
  shell: 'executando comando',
};

function capitalizeFirstPt(value: string): string {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase('pt-BR') + value.slice(1);
}

/**
 * Nome humano PT de uma tool: mapa canônico acima; fallback pra slug legível
 * ("minha_tool_nova" → "Minha tool nova") — action_types desconhecidos NUNCA
 * quebram o feed.
 */
export function humanToolLabel(toolSlug: string | null | undefined): string {
  if (!toolSlug) return 'Ferramenta';
  const known = TOOL_HUMAN_LABELS_PTBR[toolSlug];
  if (known) return capitalizeFirstPt(known);
  return capitalizeFirstPt(toolSlug.replace(/[_-]+/g, ' ').trim()) || 'Ferramenta';
}

// ── Resumo LEGÍVEL de erro (admin: o JSON cru vazava inline na tela) ──────────
// Reconhece os padrões reais dos error_message do banco e devolve uma frase PT
// curta. NUNCA mostra o JSON/stack cru. Ordem importa (mais específico primeiro).
const ERROR_SUMMARY_RULES: Array<{ test: RegExp; label: string }> = [
  { test: /usage_limit_reached|usage limit has been/i, label: 'Limite de uso da conta atingido' },
  { test: /rate.?limit|http_429|429/i, label: 'Limite de requisições atingido (rate limit)' },
  { test: /blocked_by_breaker|circuit.?breaker/i, label: 'Bloqueado — sem conta LLM disponível' },
  { test: /no_active_accounts|sem conta dispon|all_providers_exhausted|all_retries_failed|no_account_available|NoAccountAvailable/i, label: 'Sem conta LLM disponível' },
  { test: /duplicate key.*idempotency|tasks_idempotency/i, label: 'Tarefa duplicada (idempotência)' },
  { test: /column .* does not exist|42703/i, label: 'Erro de SQL (coluna inexistente)' },
  { test: /syntax error|42601/i, label: 'Erro de sintaxe SQL' },
  { test: /sql_error/i, label: 'Erro de SQL' },
  // DEM-2633: statement timeout do Postgres ≠ timeout de skill/rede.
  // Ordem importa — esta regra TEM que vir antes da genérica /timeout/.
  { test: /canceling statement due to statement timeout|select_failed:canceling statement/i, label: 'Consulta ao banco estourou o tempo' },
  { test: /timeout|timed out|ETIMEDOUT/i, label: 'Tempo de execução esgotado' },
  { test: /invalid_grant|oauth|token.*(expired|revoked)/i, label: 'Credencial expirada — reconexão necessária' },
];

/**
 * Resume um error_message cru numa frase PT curta e legível pro feed. Extrai o
 * miolo de wrappers `{ok:false,error:X}` / `{message:X}` antes de casar as regras;
 * sem padrão conhecido, remove o JSON e humaniza o código do início. Nunca
 * devolve o dump cru inteiro.
 */
export function summarizeActivityError(raw: string | null | undefined): string | null {
  const clean = sanitizeErrorMessage(raw);
  if (!clean) return null;

  // Desembrulha wrappers JSON aninhados ({ok,error} / {message}) até o texto real.
  let core = clean;
  for (let i = 0; i < 4; i++) {
    const m = core.match(/"(?:error|message|detail)"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    if (!m) break;
    core = m[1]!.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }

  for (const rule of ERROR_SUMMARY_RULES) {
    if (rule.test.test(core) || rule.test.test(clean)) return rule.label;
  }

  // Fallback: corta o JSON (primeiro '{'), pega o último segmento de código
  // (após ':'), humaniza snake_case e trunca. Ex.:
  // "agent_runner_llm_threw:analyst:foo_bar {json}" → "Foo bar".
  const beforeJson = core.split('{')[0]!.trim().replace(/[:\-\s]+$/, '');
  const seg = beforeJson.split(':').filter(Boolean).pop() ?? beforeJson;
  const humanized = seg.replace(/_/g, ' ').trim();
  if (!humanized) return 'Falha na execução';
  const capped = humanized.length > 80 ? humanized.slice(0, 80) + '…' : humanized;
  return capped.charAt(0).toUpperCase() + capped.slice(1);
}

/**
 * Verbo/frase de estado por status, com concordância de gênero com o SUJEITO
 * da linha ("Tarefa … executada" / "Pipeline … executado"). `done` é a forma
 * concluída específica do sujeito ("executada", "processada", "concluído").
 */
function statusPhrase(
  status: ActivityStatus,
  done: string,
  feminine = false,
): string {
  switch (status) {
    case 'running': return 'em execução';
    case 'completed': return done;
    case 'failed': return 'falhou';
    case 'timeout': return 'falhou por timeout';
    case 'cancelled': return feminine ? 'cancelada' : 'cancelado';
    case 'partial': return feminine ? 'concluída parcialmente' : 'concluído parcialmente';
  }
}

/**
 * Sufixo de status pra linhas cujo SUJEITO já é a ação em gerúndio (tools:
 * "Rodando comando"). Concluído = sem sufixo (o dot verde/✅ diz isso), igual
 * ao histórico do Telegram ("✅ Rodando comando" / "⚠️ … — falhou").
 */
function gerundStatusSuffix(status: ActivityStatus): string {
  switch (status) {
    case 'running': return '(em execução)';
    case 'completed': return '';
    case 'failed': return '— falhou';
    case 'timeout': return '— falhou por timeout';
    case 'cancelled': return '— cancelada';
    case 'partial': return '— parcial';
  }
}

export function formatActorLead(item: ActivityItem): ActorLead {
  const slug = item.actor_slug ?? item.actor_name ?? item.kind;
  const name = item.actor_name ?? slug;

  if (item.kind === 'agent') {
    return {
      display: name,
      code: item.actor_slug && item.actor_slug !== name ? item.actor_slug : null,
      verb: `executou como agente${statusSuffix(item.status)}`,
    };
  }
  if (item.kind === 'skill') {
    // Com invocador conhecido: "Priscila disparou a skill X". Sem: direto ao
    // ponto — "Skill executada <slug>" (sem "Execução de skill rodou a skill").
    if (item.parent_actor_name) {
      return {
        display: item.parent_actor_name,
        verb: `disparou a skill${statusSuffix(item.status)}`,
        target: name,
      };
    }
    return { display: 'Skill', verb: statusPhrase(item.status, 'executada', true), target: name };
  }
  if (item.kind === 'companion') {
    return { display: name.replace(/^@/, ''), verb: `executou como companion${statusSuffix(item.status)}` };
  }
  if (item.kind === 'handler') {
    // RPC nova resolve o agente real em actor_name pros @agent-runner.
    if (isResolvedAgentName(item.actor_name)) {
      return {
        display: item.actor_name!,
        verb: `executou como agente${statusSuffix(item.status)}`,
        target: item.target_slug && item.target_slug !== item.actor_slug ? item.target_slug : null,
      };
    }
    // ── Runners de INFRAESTRUTURA — wording humano 2026-08-04 (regra do dono:
    // claro e concreto; nada de "Execução de tarefa executou"). ─────────────
    if (name === '@task-runner') {
      // "Tarefa 3f2a91c0 executada" — a tarefa é o sujeito, com id curto.
      const shortId = item.task_id ? item.task_id.slice(0, 8) : null;
      return {
        display: shortId ? `Tarefa ${shortId}` : 'Tarefa',
        verb: statusPhrase(item.status, 'executada', true),
        target: item.target_slug ?? null,
      };
    }
    if (name === '@tool-runner') {
      // Nome HUMANO da tool em PT (mapa canônico compartilhado com o Telegram):
      // "Rodando comando run_shell" — nunca "Ferramenta usou a ferramenta X".
      if (item.target_slug) {
        return {
          display: humanToolLabel(item.target_slug),
          verb: gerundStatusSuffix(item.status),
          target: item.target_slug,
        };
      }
      return { display: 'Ferramenta', verb: statusPhrase(item.status, 'executada', true), target: null };
    }
    if (name === '@skill-runner') {
      return { display: 'Skill', verb: statusPhrase(item.status, 'executada', true), target: item.target_slug ?? null };
    }
    if (name === '@demand-runner') {
      return { display: 'Demanda', verb: statusPhrase(item.status, 'processada', true), target: item.target_slug ?? null };
    }
    if (name === '@agent-runner') {
      // Sem agente resolvido não inventamos nome — mas a frase continua inteira.
      return { display: 'Agente', verb: statusPhrase(item.status, 'concluiu a execução'), target: item.target_slug ?? null };
    }
    // Demais runners (@pipeline-runner, @ephemeral-runner, novos): nome
    // humanizado + estado direto ("Pipeline em execução", "Backup concluído") —
    // fallback garantido pra action_types desconhecidos.
    return {
      display: humanizeRunnerName(name),
      verb: statusPhrase(item.status, 'concluído'),
      target: item.target_slug ?? null,
    };
  }
  // ephemeral/wizard: runner honesto humanizado + estado direto (sem
  // "X executou" solto).
  return {
    display: humanizeRunnerName(item.actor_name) || getRunnerLabel(item.actor_name),
    verb: statusPhrase(item.status, 'concluído'),
    target: item.target_slug ?? null,
  };
}

/* ── Adapter: execuções da demanda (GET /demands/:id/executions) ────────────── */
//
// A aba Atividade da demanda consome DemandExecutionRow (shape do endpoint),
// não ActivityItem (shape do RPC do feed). Este adapter converte o mínimo
// necessário e reusa formatActorLead — UMA voz de produto nos dois feeds.

/** Normaliza status heterogêneo das exec tables pro vocabulário do feed. */
export function normalizeActivityStatus(status: string | null | undefined): ActivityStatus {
  const s = (status ?? '').toLowerCase();
  if (['done', 'completed', 'ok', 'success'].includes(s)) return 'completed';
  if (['failed', 'error'].includes(s)) return 'failed';
  if (s === 'timeout') return 'timeout';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'partial') return 'partial';
  // running/executing/pending/queued/desconhecido → "em execução" (não é
  // terminal; afirmar conclusão ou falha sem evidência seria mentira).
  return 'running';
}

export interface ExecutionLeadInput {
  kind: 'handler' | 'agent' | 'skill' | 'tool';
  status: string | null;
  actor_name?: string | null;
  actor_slug?: string | null;
  handler_name?: string | null;
  agent_id?: string | null;
  skill_id?: string | null;
  task_id?: string | null;
  /** Alvo do runner (tool/agent/skill slug) quando conhecido pelo caller. */
  target_slug?: string | null;
}

/** Item mínimo pro formatador único — os demais campos não influenciam o lead. */
function leadItem(over: Partial<ActivityItem> & Pick<ActivityItem, 'kind' | 'status'>): ActivityItem {
  return {
    id: '', actor_id: null, actor_slug: null, actor_name: null,
    actor_archetype: null, actor_role: null, actor_scope: null,
    parent_actor_id: null, parent_actor_slug: null, parent_actor_name: null,
    invoker_type: null, started_at: '', ended_at: null, duration_ms: null,
    cost_usd: null, tokens_input: null, tokens_output: null, model_used: null,
    task_id: null, trace_id: null, error_message: null, parent_trace_id: null,
    root_trace_id: null, metadata_kind: null, llm_calls_count: null,
    tool_calls_count: null, target_slug: null, cost_usd_partial: null,
    tokens_input_partial: null, tokens_output_partial: null,
    children_completed_count: null,
    ...over,
  };
}

export function formatExecutionLead(ev: ExecutionLeadInput): ActorLead {
  const status = normalizeActivityStatus(ev.status);
  if (ev.kind === 'tool') {
    // tool_executions (caminho legacy) — mesma linha do handler '@tool-runner'.
    return formatActorLead(leadItem({
      kind: 'handler', status, actor_name: '@tool-runner', actor_slug: '@tool-runner',
      task_id: ev.task_id ?? null, target_slug: ev.target_slug ?? null,
    }));
  }
  if (ev.kind === 'agent') {
    // Sem nome resolvido não fingimos um: cai na linha honesta do @agent-runner.
    if (!ev.actor_name) {
      return formatActorLead(leadItem({
        kind: 'handler', status, actor_name: '@agent-runner', actor_slug: '@agent-runner',
        task_id: ev.task_id ?? null, target_slug: ev.target_slug ?? null,
      }));
    }
    return formatActorLead(leadItem({
      kind: 'agent', status, actor_name: ev.actor_name, actor_slug: ev.actor_slug ?? null,
      task_id: ev.task_id ?? null,
    }));
  }
  if (ev.kind === 'skill') {
    return formatActorLead(leadItem({
      kind: 'skill', status,
      actor_name: ev.actor_name ?? ev.actor_slug ?? ev.skill_id ?? null,
      actor_slug: ev.actor_slug ?? null,
      task_id: ev.task_id ?? null,
    }));
  }
  return formatActorLead(leadItem({
    kind: 'handler', status,
    actor_name: ev.actor_name ?? ev.handler_name ?? null,
    actor_slug: ev.actor_slug ?? ev.handler_name ?? null,
    task_id: ev.task_id ?? null,
    target_slug: ev.target_slug ?? null,
  }));
}
